<template>
  <div class="mb-4 rounded-box border border-base-300 bg-base-100 p-4">
    <div class="flex items-center justify-between">
      <div :class="isRtl === true ? 'ml-4' : 'mr-4'">
        <div class="font-semibold" :style="labelStyle">
          {{ t('aiChatAgentSetup.voiceSettings.transcriptionOptimizer', 'Transcription optimizer') }}
        </div>
        <p class="mt-1 text-xs text-base-content/60" :style="labelStyle">
          {{ t('aiChatAgentSetup.voiceSettings.transcriptionOptimizerHint', 'Generate domain-specific words from the agent prompt to improve speech recognition.') }}
        </p>
        <span v-if="providerLabel !== ''" class="badge badge-sm mt-2">{{ providerLabel }}</span>
      </div>
      <input
        type="checkbox"
        class="toggle toggle-accent"
        :checked="enabled === true"
        @change="emit('update:enabled', $event.target.checked === true)"
      />
    </div>

    <div class="mt-4 flex flex-wrap items-center">
      <button
        type="button"
        class="btn btn-sm mr-2 mb-2"
        :disabled="isGenerating === true || sourcePrompt.trim() === ''"
        @click="generateTerms"
      >
        <span v-if="isGenerating === true" class="loading loading-spinner loading-xs"></span>
        {{ t('aiChatAgentSetup.voiceSettings.generateTranscriptionTerms', 'Generate') }}
      </button>
      <button type="button" class="btn btn-sm mr-2 mb-2" @click="openEditor">
        {{ t('aiChatAgentSetup.voiceSettings.editTranscriptionTerms', 'View words') }}
        <span class="badge badge-sm">{{ normalizedTerms.length }}</span>
      </button>
      <span class="text-xs" :class="enabled === true ? 'text-success' : 'text-base-content/50'">
        {{ enabled === true
          ? t('aiChatAgentSetup.voiceSettings.transcriptionOptimizerEnabled', 'Enabled for calls')
          : t('aiChatAgentSetup.voiceSettings.transcriptionOptimizerDisabled', 'Disabled for calls') }}
      </span>
    </div>

    <Teleport to="body">
      <div
        v-if="editorOpen === true"
        class="fixed inset-0 z-[260] flex items-center justify-center bg-black/55 px-4 py-6"
        @click="closeEditor"
      >
        <div class="w-full max-w-xl rounded-xl bg-base-100 shadow-2xl" :dir="isRtl === true ? 'rtl' : 'ltr'" @click.stop>
          <div class="flex items-center justify-between border-b border-base-300 px-6 py-4">
            <div>
              <h3 class="text-lg font-bold" :style="labelStyle">
                {{ t('aiChatAgentSetup.voiceSettings.transcriptionTermsTitle', 'Transcription words') }}
              </h3>
              <p class="mt-1 text-xs text-base-content/60" :style="labelStyle">
                {{ t('aiChatAgentSetup.voiceSettings.transcriptionTermsHint', 'Review, add, remove, or correct words before saving the agent.') }}
              </p>
            </div>
            <button type="button" class="btn btn-ghost btn-sm" @click="closeEditor">X</button>
          </div>

          <div class="max-h-[60vh] overflow-y-auto px-6 py-5">
            <div v-for="(term, index) in draftTerms" :key="`transcription-term-${index}`" class="mb-3 flex items-center">
              <input
                v-model="draftTerms[index]"
                type="text"
                maxlength="80"
                class="input input-bordered flex-1 mr-2"
                :dir="inputDirection"
                :placeholder="t('aiChatAgentSetup.voiceSettings.transcriptionTermPlaceholder', 'Word or short phrase')"
              />
              <button type="button" class="btn btn-error btn-sm" @click="removeTerm(index)">
                {{ t('aiChatAgentSetup.buttons.delete', 'Delete') }}
              </button>
            </div>
            <p v-if="draftTerms.length === 0" class="mb-3 text-sm text-base-content/60" :style="labelStyle">
              {{ t('aiChatAgentSetup.voiceSettings.noTranscriptionTerms', 'No words generated yet.') }}
            </p>
            <button type="button" class="btn btn-sm" :disabled="draftTerms.length >= 80" @click="addTerm">
              {{ t('aiChatAgentSetup.voiceSettings.addTranscriptionTerm', 'Add word') }}
            </button>
          </div>

          <div class="flex justify-end border-t border-base-300 px-6 py-4">
            <button type="button" class="btn mr-2" @click="closeEditor">
              {{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}
            </button>
            <button type="button" class="btn" @click="saveTerms">
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useToast } from 'vue-toastification';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';

const props = defineProps({
  enabled: { type: Boolean, default: false },
  terms: { type: Array, default: () => [] },
  sourcePrompt: { type: String, default: '' },
  language: { type: String, default: '' },
  providerLabel: { type: String, default: '' },
  isRtl: { type: Boolean, default: false }
});

const emit = defineEmits(['update:enabled', 'update:terms']);
const { t } = useI18n();
const toast = useToast();
const isGenerating = ref(false);
const editorOpen = ref(false);
const draftTerms = ref([]);

function normalizeTerms(valuesParam) {
  const values = Array.isArray(valuesParam) === true ? valuesParam : [];
  const output = [];
  const seen = new Set();
  for (let index = 0; index < values.length; index = index + 1) {
    const term = typeof values[index] === 'string'
      ? values[index].replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 80)
      : '';
    const key = term.toLocaleLowerCase();
    if (term.length < 2 || seen.has(key) === true) {
      continue;
    }
    seen.add(key);
    output.push(term);
    if (output.length >= 80) {
      break;
    }
  }
  return output;
}

function getAuthHeaders() {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('authToken=') === true || row.startsWith('jwtToken=') === true);
  const headers = { 'Content-Type': 'application/json' };
  if (cookie !== undefined) {
    const token = cookie.substring(cookie.indexOf('=') + 1);
    if (token !== '') {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  return headers;
}

const normalizedTerms = computed(() => normalizeTerms(props.terms));
const labelStyle = computed(() => props.isRtl === true
  ? { direction: 'rtl', textAlign: 'right' }
  : { direction: 'ltr', textAlign: 'left' });
const inputDirection = computed(() => {
  const language = typeof props.language === 'string' ? props.language.trim().toLowerCase() : '';
  return language === 'he' || language === 'hebrew' || language === 'עברית' || language === 'ar' || language === 'arabic'
    ? 'rtl'
    : 'ltr';
});

function openEditor() {
  draftTerms.value = normalizedTerms.value.slice();
  editorOpen.value = true;
}

function closeEditor() {
  editorOpen.value = false;
}

function addTerm() {
  if (draftTerms.value.length < 80) {
    draftTerms.value.push('');
  }
}

function removeTerm(indexParam) {
  if (indexParam >= 0 && indexParam < draftTerms.value.length) {
    draftTerms.value.splice(indexParam, 1);
  }
}

function saveTerms() {
  const terms = normalizeTerms(draftTerms.value);
  draftTerms.value = terms.slice();
  emit('update:terms', terms);
  editorOpen.value = false;
}

async function generateTerms() {
  const prompt = typeof props.sourcePrompt === 'string' ? props.sourcePrompt.trim() : '';
  if (prompt === '') {
    toast.warning(t('aiChatAgentSetup.voiceSettings.transcriptionPromptRequired', 'Add an agent prompt before generating words.'));
    return;
  }
  isGenerating.value = true;
  try {
    const response = await axios.post(
      `${globalState.apiEndpoint}/api/transcriptionOptimizer/generate`,
      { prompt, language: props.language },
      { headers: getAuthHeaders(), timeout: 60000 }
    );
    const terms = normalizeTerms(response?.data?.terms);
    if (terms.length === 0) {
      throw new Error('No terms returned');
    }
    emit('update:terms', terms);
    draftTerms.value = terms.slice();
    editorOpen.value = true;
    toast.success(t('aiChatAgentSetup.voiceSettings.transcriptionTermsGenerated', 'Transcription words generated.'));
  } catch (error) {
    const message = typeof error?.response?.data?.error === 'string'
      ? error.response.data.error
      : t('aiChatAgentSetup.voiceSettings.transcriptionTermsGenerationFailed', 'Could not generate transcription words.');
    toast.error(message);
  } finally {
    isGenerating.value = false;
  }
}
</script>
